import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, BookOpen, Sparkles, Check, Palette } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = ['Januari','Februari','Mars','April','Maj','Juni','Juli','Augusti','September','Oktober','November','December'];
const WEEK_LETTERS = ['M','T','O','T','F','L','S'];
const WEEK_LABELS = ['Mån','Tis','Ons','Tor','Fre','Lör','Sön'];

// Debounce hook for auto-save
function useDebouncedEffect(effect, deps, delay) {
  const cb = useRef(effect);
  useEffect(() => { cb.current = effect; }, [effect]);
  useEffect(() => {
    const t = setTimeout(() => cb.current(), delay);
    return () => clearTimeout(t);
  }, [...deps, delay]);
}

// ---------- SECTIONS ----------
function CoverSection({ theme, monthName, year, completion, intention, onIntention, onOpenThemes }) {
  const darkText = theme.dark_text;
  return (
    <div className="neo-card p-6 relative overflow-hidden"
      style={{ background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.warm} 100%)`,
               color: darkText ? theme.colors.text : theme.colors.text }}
      data-testid="planner-cover">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-widest opacity-70">Månadens tema</p>
          <div className="flex items-end gap-2 mt-1">
            <h1 className="text-4xl md:text-5xl font-bold font-['Fredoka'] leading-none">
              {monthName}
            </h1>
            <span className="text-2xl">{theme.emoji}</span>
          </div>
          <p className="text-lg md:text-xl italic mt-1 opacity-85">— {theme.name} —</p>
          <p className="text-sm mt-3 italic max-w-xl">&quot;{theme.quote}&quot;</p>
        </div>
        <button onClick={onOpenThemes}
          className="neo-btn px-3 py-2 bg-white text-black text-xs font-bold flex items-center gap-1"
          data-testid="planner-open-themes">
          <Palette className="w-3.5 h-3.5" /> Byt tema
        </button>
      </div>

      <div className="mt-5">
        <p className="text-xs font-bold uppercase tracking-widest opacity-70">Min intention</p>
        <textarea value={intention || ''} onChange={(e) => onIntention(e.target.value)}
          placeholder={`En mjuk avsikt för ${monthName.toLowerCase()}…`}
          rows={2}
          maxLength={500}
          className="w-full mt-1 bg-white/50 rounded-xl border-2 border-black/20 focus:border-black/60 outline-none px-3 py-2 text-sm resize-none"
          style={{ color: theme.colors.text }}
          data-testid="planner-intention" />
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="opacity-70">{completion}% ifyllt</span>
          <span className="opacity-60">{year}</span>
        </div>
        <div className="h-2 rounded-full bg-white/40 overflow-hidden">
          <div className="h-full transition-all"
            style={{ width: `${completion}%`, background: theme.colors.accent }} />
        </div>
      </div>
    </div>
  );
}

function CalendarSection({ theme, daysInMonth, moods, moodPalette, calendarNotes, onSetMood, onSetNote, year, month }) {
  const firstDay = new Date(year, month - 1, 1).getDay(); // 0=Sun
  const offset = (firstDay + 6) % 7; // Mon=0
  const [pickerDay, setPickerDay] = useState(null);
  const [noteText, setNoteText] = useState('');

  const openDay = (day) => {
    setPickerDay(day);
    setNoteText(calendarNotes[String(day)] || '');
  };
  const saveNote = () => {
    onSetNote(String(pickerDay), noteText);
    setPickerDay(null);
  };

  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="neo-card p-5" style={{ background: theme.colors.warm, color: theme.colors.text }}
      data-testid="planner-calendar">
      <p className="text-xs font-bold uppercase tracking-widest opacity-70 mb-3">
        📅 Månadsöversikt · Färglägg + skriv smånoter
      </p>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold opacity-70 mb-1">
        {WEEK_LETTERS.map((l, i) => <div key={i}>{l}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />;
          const mood = moods[String(d)];
          const moodObj = moodPalette.find((m) => m.id === mood);
          const note = calendarNotes[String(d)];
          return (
            <button key={i} onClick={() => openDay(d)}
              className="aspect-square rounded-lg border-2 border-black/20 hover:border-black/60 text-xs font-bold relative flex items-center justify-center transition"
              style={{ backgroundColor: moodObj?.color || 'rgba(255,255,255,0.6)' }}
              data-testid={`planner-day-${d}`}>
              <span>{d}</span>
              {note && <span className="absolute bottom-0.5 right-0.5 text-[8px]">✍️</span>}
            </button>
          );
        })}
      </div>

      {pickerDay !== null && (
        <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPickerDay(null)}>
          <div className="neo-card bg-white max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold font-['Fredoka'] mb-3">{pickerDay} {MONTH_NAMES[month - 1]}</h3>
            <p className="text-xs font-bold mb-2">Hur kändes dagen?</p>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {moodPalette.map((m) => (
                <button key={m.id}
                  onClick={() => { onSetMood(String(pickerDay), moods[String(pickerDay)] === m.id ? '' : m.id); }}
                  className={`px-2 py-1 rounded-full border-2 border-black text-[10px] font-bold flex items-center gap-1 ${moods[String(pickerDay)] === m.id ? 'scale-110' : ''}`}
                  style={{ backgroundColor: m.color }}>
                  {m.emoji} {m.label}
                </button>
              ))}
            </div>
            <label className="block text-xs font-bold mb-3">Liten anteckning
              <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)}
                placeholder="En rad om dagen…"
                rows={3} maxLength={200}
                className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-black bg-white text-sm resize-none" />
            </label>
            <div className="flex gap-2">
              <button onClick={() => setPickerDay(null)} className="neo-btn flex-1 py-2 bg-white text-xs font-bold">Stäng</button>
              <button onClick={saveNote} className="neo-btn flex-1 py-2 bg-[#BBF7D0] text-xs font-bold">Spara</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HabitsSection({ theme, habits, daysInMonth, onToggleHabit, onRenameHabit }) {
  return (
    <div className="neo-card p-5" style={{ background: theme.colors.warm, color: theme.colors.text }}
      data-testid="planner-habits">
      <p className="text-xs font-bold uppercase tracking-widest opacity-70 mb-3">
        🌱 Mina vanor · Sex mjuka rutiner
      </p>
      <div className="space-y-2">
        {habits.map((h, hi) => (
          <div key={h.id} className="rounded-xl border-2 border-black/20 bg-white/70 p-2" data-testid={`planner-habit-${h.id}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">{h.emoji}</span>
              <input value={h.name}
                onChange={(e) => onRenameHabit(hi, e.target.value)}
                className="flex-1 bg-transparent text-sm font-bold outline-none border-b border-transparent focus:border-black/40"
                maxLength={40} />
            </div>
            <div className="grid grid-cols-[repeat(31,minmax(0,1fr))] gap-0.5">
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => {
                const done = h.days?.[String(d)];
                return (
                  <button key={d}
                    onClick={() => onToggleHabit(hi, d, !done)}
                    className="aspect-square rounded-sm border border-black/40 text-[7px] flex items-center justify-center transition"
                    style={{ backgroundColor: done ? theme.colors.accent : 'transparent',
                             color: done ? '#fff' : theme.colors.text }}
                    title={`Dag ${d}`}>
                    {done ? '✓' : d}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MoodSection({ theme, moods, moodPalette, daysInMonth }) {
  const counts = useMemo(() => {
    const c = Object.fromEntries(moodPalette.map((m) => [m.id, 0]));
    Object.values(moods || {}).forEach((v) => { if (c[v] !== undefined) c[v]++; });
    return c;
  }, [moods, moodPalette]);

  return (
    <div className="neo-card p-5" style={{ background: theme.colors.warm, color: theme.colors.text }}
      data-testid="planner-moods">
      <p className="text-xs font-bold uppercase tracking-widest opacity-70 mb-3">
        🎨 Humörspårning · Färglägg månaden
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
        {moodPalette.map((m) => (
          <div key={m.id} className="rounded-xl border-2 border-black p-2 flex items-center gap-2"
            style={{ backgroundColor: m.color, color: '#111' }}>
            <span className="text-lg">{m.emoji}</span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold truncate">{m.label}</p>
              <p className="text-[10px] opacity-70">{counts[m.id]} dagar</p>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs opacity-70 italic">
        Färglägg genom att trycka på en dag i kalendern ovan 🌈
      </p>
      <div className="mt-3 flex flex-wrap gap-0.5">
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => {
          const mood = moods[String(d)];
          const moodObj = moodPalette.find((m) => m.id === mood);
          return (
            <div key={d} className="w-6 h-6 rounded border border-black/30 flex items-center justify-center text-[9px] font-bold"
              style={{ backgroundColor: moodObj?.color || 'rgba(255,255,255,0.5)' }}
              title={`Dag ${d}`}>{d}</div>
          );
        })}
      </div>
    </div>
  );
}

function WeekSection({ theme, weeks, onUpdateWeek }) {
  const [openIdx, setOpenIdx] = useState(0);
  const current = weeks[openIdx] || { title: '', entries: {} };
  return (
    <div className="neo-card p-5" style={{ background: theme.colors.warm, color: theme.colors.text }}
      data-testid="planner-weeks">
      <p className="text-xs font-bold uppercase tracking-widest opacity-70 mb-3">
        📖 Veckoplanering · Fem fria uppslag
      </p>
      <div className="flex gap-1 flex-wrap mb-3">
        {weeks.map((w, i) => (
          <button key={i} onClick={() => setOpenIdx(i)}
            className={`px-3 py-1.5 rounded-full border-2 border-black text-xs font-bold ${openIdx === i ? 'bg-white scale-105' : 'bg-white/50'}`}
            data-testid={`planner-week-tab-${i}`}>
            V.{i + 1}{w.title ? ` · ${w.title.slice(0, 12)}` : ''}
          </button>
        ))}
      </div>
      <input value={current.title || ''} onChange={(e) => onUpdateWeek(openIdx, 'title', e.target.value)}
        placeholder="Rubrik för veckan…"
        maxLength={60}
        className="w-full px-3 py-2 rounded-xl border-2 border-black bg-white text-sm font-bold mb-3"
        data-testid="planner-week-title" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {WEEK_LABELS.map((lbl, i) => (
          <div key={i} className="rounded-xl border-2 border-black bg-white p-2">
            <p className="text-[10px] font-bold uppercase opacity-70 mb-1">{lbl}</p>
            <textarea value={current.entries?.[lbl] || ''}
              onChange={(e) => onUpdateWeek(openIdx, 'entries', { ...current.entries, [lbl]: e.target.value })}
              placeholder="Vad händer…"
              rows={2} maxLength={400}
              className="w-full bg-transparent outline-none text-sm resize-none"
              data-testid={`planner-week-${openIdx}-${lbl}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

function NotesSection({ theme, notes, onNotes }) {
  return (
    <div className="neo-card p-5" style={{ background: theme.colors.warm, color: theme.colors.text }}
      data-testid="planner-notes">
      <p className="text-xs font-bold uppercase tracking-widest opacity-70 mb-3">
        💭 Små tankar & stora idéer
      </p>
      <textarea value={notes || ''} onChange={(e) => onNotes(e.target.value)}
        placeholder="Fritext för allt som dyker upp under månaden — drömmar, listor, citat, planer…"
        rows={10} maxLength={3000}
        className="w-full px-3 py-3 rounded-xl border-2 border-black bg-white text-sm resize-none"
        data-testid="planner-notes-input" />
    </div>
  );
}

function ZinniaWidget({ year, month }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/zinnia/month/${year}/${month}`, { withCredentials: true })
      .then((r) => setData(r.data.spread))
      .catch(() => setData(null));
  }, [year, month]);
  if (!data) return null;
  return (
    <div className="neo-card p-4 bg-gradient-to-br from-[#FDE8F5] to-[#E8F4FF]" data-testid="planner-zinnia-widget">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold uppercase tracking-widest opacity-70">🌸 Från din Zinnia-månad</p>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="rounded-xl border-2 border-black bg-white p-2 text-center">
          <p className="text-lg font-bold font-['Fredoka']">{data.birthdays?.length || 0}</p>
          <p className="text-[10px] opacity-70">🎂 datum</p>
        </div>
        <div className="rounded-xl border-2 border-black bg-white p-2 text-center">
          <p className="text-lg font-bold font-['Fredoka']">{data.photos?.length || 0}</p>
          <p className="text-[10px] opacity-70">📸 foton</p>
        </div>
        <div className="rounded-xl border-2 border-black bg-white p-2 text-center">
          <p className="text-lg font-bold font-['Fredoka']">{data.notes?.length || 0}</p>
          <p className="text-[10px] opacity-70">📝 lappar</p>
        </div>
      </div>
      {data.title && <p className="text-xs mt-2 italic opacity-70">&quot;{data.title}&quot;</p>}
    </div>
  );
}

// ---------- MAIN ----------
export default function MinPlanner() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [themes, setThemes] = useState([]);
  const [moodPalette, setMoodPalette] = useState([]);
  const [spread, setSpread] = useState(null);
  const [theme, setTheme] = useState(null);
  const [daysInMonth, setDaysInMonth] = useState(30);
  const [completion, setCompletion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showThemes, setShowThemes] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setDirty(false);
    try {
      const [t, m] = await Promise.all([
        axios.get(`${API}/planner/themes`, { withCredentials: true }),
        axios.get(`${API}/planner/month/${year}/${month}`, { withCredentials: true }),
      ]);
      setThemes(t.data.themes || []);
      setMoodPalette(t.data.mood_palette || []);
      setSpread(m.data.spread);
      setTheme(m.data.theme);
      setDaysInMonth(m.data.days_in_month);
      setCompletion(m.data.completion);
    } catch (e) {
      toast.error('Kunde inte ladda planner');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  // Auto-save on dirty change (debounced 1200ms)
  useDebouncedEffect(async () => {
    if (!dirty || !spread) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/planner/month/${year}/${month}`, {
        intention: spread.intention,
        habits: spread.habits,
        moods: spread.moods,
        calendar_notes: spread.calendar_notes,
        weeks: spread.weeks,
        notes: spread.notes,
        theme_override: spread.theme_override,
      }, { withCredentials: true });
      setCompletion(data.completion);
      setTheme(data.theme);
      setDirty(false);
    } catch (e) {
      // silent
    } finally {
      setSaving(false);
    }
  }, [spread, dirty], 1200);

  const patch = (fn) => {
    setSpread((s) => fn({ ...s }));
    setDirty(true);
  };

  const changeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setYear(y); setMonth(m);
  };

  if (loading || !spread || !theme) {
    return <div className="neo-card p-8 text-center animate-pulse-soft">Öppnar din planner…</div>;
  }

  const monthName = MONTH_NAMES[month - 1];

  return (
    <div className="space-y-4 pb-16" data-testid="min-planner">
      {/* Month nav */}
      <div className="neo-card p-3 flex items-center justify-between flex-wrap gap-2 bg-white dark:bg-[#252545]">
        <div className="flex items-center gap-2">
          <button onClick={() => changeMonth(-1)} className="neo-btn p-2 bg-white" data-testid="planner-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="text-center min-w-[160px]">
            <p className="text-[10px] uppercase tracking-widest opacity-60">{year}</p>
            <p className="text-xl font-bold font-['Fredoka']">{monthName}</p>
          </div>
          <button onClick={() => changeMonth(1)} className="neo-btn p-2 bg-white" data-testid="planner-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {saving ? <span className="opacity-70">Sparar…</span>
            : dirty ? <span className="opacity-70">Sparar snart…</span>
            : <span className="text-emerald-600 font-bold flex items-center gap-1"><Check className="w-3 h-3" /> Sparat</span>}
        </div>
      </div>

      <CoverSection theme={theme} monthName={monthName} year={year}
        completion={completion}
        intention={spread.intention}
        onIntention={(v) => patch((s) => ({ ...s, intention: v }))}
        onOpenThemes={() => setShowThemes(true)} />

      <ZinniaWidget year={year} month={month} />

      <CalendarSection theme={theme} year={year} month={month}
        daysInMonth={daysInMonth}
        moods={spread.moods || {}}
        moodPalette={moodPalette}
        calendarNotes={spread.calendar_notes || {}}
        onSetMood={(d, m) => patch((s) => ({ ...s, moods: { ...(s.moods || {}), [d]: m } }))}
        onSetNote={(d, txt) => patch((s) => ({ ...s, calendar_notes: { ...(s.calendar_notes || {}), [d]: txt } }))} />

      <HabitsSection theme={theme}
        habits={spread.habits || []}
        daysInMonth={daysInMonth}
        onToggleHabit={(hi, d, val) => patch((s) => {
          const habits = [...(s.habits || [])];
          habits[hi] = { ...habits[hi], days: { ...(habits[hi].days || {}), [String(d)]: val } };
          return { ...s, habits };
        })}
        onRenameHabit={(hi, name) => patch((s) => {
          const habits = [...(s.habits || [])];
          habits[hi] = { ...habits[hi], name };
          return { ...s, habits };
        })} />

      <MoodSection theme={theme} moods={spread.moods || {}} moodPalette={moodPalette} daysInMonth={daysInMonth} />

      <WeekSection theme={theme}
        weeks={spread.weeks || []}
        onUpdateWeek={(i, key, val) => patch((s) => {
          const weeks = [...(s.weeks || [])];
          weeks[i] = { ...(weeks[i] || { index: i }), [key]: val };
          return { ...s, weeks };
        })} />

      <NotesSection theme={theme} notes={spread.notes}
        onNotes={(v) => patch((s) => ({ ...s, notes: v }))} />

      {showThemes && (
        <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowThemes(false)} data-testid="planner-theme-picker">
          <div className="neo-card bg-white max-w-2xl w-full p-5 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold font-['Fredoka'] mb-3">Byt tema för {monthName}</h3>
            <p className="text-xs opacity-70 mb-3">Standardtemat är kopplat till månaden, men du kan välja fritt.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {themes.map((t) => (
                <button key={t.id}
                  onClick={() => { patch((s) => ({ ...s, theme_override: t.id === month ? null : t.id })); setShowThemes(false); }}
                  className={`p-4 rounded-2xl border-2 border-black text-left transition hover:scale-[1.02] ${theme.id === t.id ? 'ring-4 ring-pink-400' : ''}`}
                  style={{ background: `linear-gradient(135deg, ${t.colors.primary} 0%, ${t.colors.warm} 100%)`,
                           color: t.colors.text }}
                  data-testid={`planner-theme-${t.id}`}>
                  <div className="text-2xl">{t.emoji}</div>
                  <p className="text-sm font-bold font-['Fredoka'] mt-1">{t.name}</p>
                  <p className="text-[10px] italic opacity-80">{t.sub}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
